import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/ui/sonner";

const STORAGE_KEY = "finncreators-prompt-library";

const sanitizePromptText = (text = "") =>
  text
    .replaceAll("FinnCreators-tyylisenä sisältövalmentajana", "käytännönläheisenä sisältövalmentajana")
    .replaceAll("FinnCreators-laatuperiaate", "Yleinen laatuperiaate");

const formatDateForTitle = (date) => new Intl.DateTimeFormat("fi-FI", { day: "numeric", month: "numeric", year: "numeric" }).format(date);

const sanitizeLibraryItem = (item) => ({
  ...item,
  title: item.title || `${item.type || "Prompti"} ${formatDateForTitle(item.createdAt ? new Date(item.createdAt) : new Date())}`,
  type: item.type || "Prompti",
  aiTool: item.aiTool || "",
  favorite: Boolean(item.favorite),
  prompt: sanitizePromptText(item.prompt),
  createdAt: item.createdAt || new Date().toISOString(),
});

const sortNewestFirst = (items) => [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

export const usePromptLibrary = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const sanitized = sortNewestFirst(Array.isArray(saved) ? saved.map(sanitizeLibraryItem) : []);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
      setItems(sanitized);
    } catch {
      setItems([]);
    }
  }, []);

  const savePrompt = useCallback(({ title, type, prompt, aiTool }) => {
    if (!prompt) return;
    const category = type || "Prompti";
    const now = new Date();
    const nextItem = {
      id: crypto.randomUUID(),
      title: (title || "").trim() || `${category} ${formatDateForTitle(now)}`,
      type: category,
      aiTool: (aiTool || "").trim(),
      favorite: false,
      prompt: sanitizePromptText(prompt),
      createdAt: now.toISOString(),
    };
    setItems((currentItems) => {
      const nextItems = sortNewestFirst([nextItem, ...currentItems]);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextItems));
      return nextItems;
    });
    toast.success("Prompti tallennettu kirjastoon");
  }, []);

  const removePrompt = useCallback((id) => {
    setItems((currentItems) => {
      const nextItems = currentItems.filter((item) => item.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextItems));
      return nextItems;
    });
    toast.success("Prompti poistettu kirjastosta");
  }, []);

  const toggleFavorite = useCallback((id) => {
    let isFavorite = false;
    setItems((currentItems) => {
      const nextItems = currentItems.map((item) => {
        if (item.id !== id) return item;
        isFavorite = !item.favorite;
        return { ...item, favorite: isFavorite };
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextItems));
      return nextItems;
    });
    toast.success(isFavorite ? "Lisätty suosikkeihin" : "Poistettu suosikeista");
  }, []);

  return { items, savePrompt, removePrompt, toggleFavorite };
};